import React, { useEffect, useState } from "react";
import { mapUserRoles } from "../../functions/useRoleMapping";
import { selectUserRoles } from "../../redux/slices/userSlice";
import { Button, Modal, Spinner, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchTableIds } from "../../redux/slices/tableIdSlice";
import { fetchZones } from "../../redux/slices/zoneCountSlice";
import { selectGeneralStation, selectJobDetails } from "../../redux/selectors";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import axios from "../../axios/axiosConfig";
import { enqueueApiCall } from "../../redux/slices/apiQueueSlice";
import { setSelectedZone } from "../../redux/slices/activeMechanicalTabSlice";
import { buildZoneCreationPayload, checkCanCreateZone2 } from "../../functions/useCreateZoneData";

function CreateZoneButton({ jobId, zoneCount, activeTabNumber }) {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const jobDetailsState = useSelector(selectJobDetails);
  const userRoles = useSelector(selectUserRoles);
  const [roles, setRoles] = useState(mapUserRoles(userRoles));
  
  // Get the selected zone from Redux store
  const selectedZone = useSelector((state) => state.activeMechanicalTab.selectedZone);
  
  // Use Redux state for active zone, fallback to prop if not available
  const currentActiveZone = selectedZone || activeTabNumber || 1;
  
  const [showModal, setShowModal] = useState(false);
  const [creatingZone, setCreatingZone] = useState(false);
  const [logs, setLogs] = useState([]);
  const [apiStatus, setApiStatus] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [zone2Created, setZone2Created] = useState(false);
  const [zone2ExistsState, setZone2ExistsState] = useState(null); // null = checking, true = exists, false = doesn't exist
  
  // Check if Zone 2 already exists in Redux store
  const generalStationDetails = useSelector(selectGeneralStation);
  const hasZone2InStore = generalStationDetails.some(detail => detail.Zone === 2);
  
  // Combined check: use API state if available, fallback to store data
  const hasZone2 = zone2ExistsState !== null ? zone2ExistsState : hasZone2InStore;
  
  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  // Zone 2 existence check is now done lazily when button is clicked (in handleCreateZoneClick)
  // This avoids unnecessary API calls on component mount

  // Ensure Zone 1 is selected by default if no zone is currently selected
  useEffect(() => {
    if (jobId && (!selectedZone || selectedZone === 0)) {
      dispatch(setSelectedZone(1));
    }
  }, [jobId, selectedZone, dispatch]);

  useEffect(() => {
    // Update zone2Created state based on generalStationDetails
    setZone2Created(generalStationDetails.some(detail => detail.Zone === 2));
  }, [generalStationDetails]);

  const storedJobDetails = useSelector(selectGeneralStation);
  const jobDetails = storedJobDetails?.[0] || {};

  const handleCreateZoneClick = async () => {
    // Use comprehensive check for Zone 2 creation
    const generalProjectUrl = apiUrls.generalProject;
    const generalStationUrl = apiUrls.generalStation;
    // Pass jobDetailsState as first parameter
    const canCreateResult = await checkCanCreateZone2(
      jobDetailsState,
      generalProjectUrl, 
      generalStationUrl, 
      jobId
    );
    
    if (!canCreateResult.canCreate) {
      setLogs([
        "Cannot create Zone 2:",
        canCreateResult.reason
      ]);
      setShowModal(true);
      return;
    }
    
    setShowModal(true);
    setLogs([
      `Zone creation validation passed.`,
      `General info records: ${canCreateResult.generalInfoCount}`,
      `Existing zones: [${canCreateResult.existingZones.join(', ') || 'None'}]`,
      "Proceeding with Zone 2 creation..."
    ]);
    setApiStatus({});
  };
  
  const closeModal = async () => {
    // If we're closing after showing a "Zone 2 already exists" error, 
    // update the component state to reflect this
    if (logs.length > 0 && logs[0].includes("Cannot create Zone 2") && 
        logs.some(log => log.includes("Zone 2 already exists"))) {
      setZone2ExistsState(true);
    }
    
    setShowModal(false);
    setLogs([]);
    setApiStatus({});
    setShowSuccessMessage(false);
  };
  
  const handleZoneClick = (zoneNumber) => {
    dispatch(setSelectedZone(zoneNumber));
  };
  
  const renderStatusIcon = (status) => {
    if (status === "loading") {
      return <Spinner animation="border" size="sm" />;
    } else if (status === "success") {
      return <span style={{ color: "green" }}>✔️</span>;
    } else if (status === "failed") {
      return <span style={{ color: "red" }}>❌</span>;
    }
    return null;
  };

  const createZoneData = async () => {
    if (!jobId) {
      setLogs(prev => [...prev, "Error: No job ID found"]);
      return;
    }
    
    // Double-check with comprehensive validation before proceeding
    const generalProjectUrl = apiUrls.generalProject;
    const generalStationUrl = apiUrls.generalStation;
    // Pass jobDetailsState as first parameter
    const canCreateResult = await checkCanCreateZone2(
      jobDetailsState,
      generalProjectUrl, 
      generalStationUrl, 
      jobId
    );
    
    if (!canCreateResult.canCreate) {
      setLogs(prev => [
        ...prev,
        "Cannot proceed with Zone 2 creation:",
        canCreateResult.reason
      ]);
      return;
    }
    
    setCreatingZone(true);
    setLogs(prev => [...prev, "Starting Zone 2 creation process..."]);
    
    try {
      const selectedApiUrls = [
        { name: "generalStation", data: { Zone: 2 } },
        { name: "hcRequirements", data: { Zone: 2 } },
        { name: "skidConstruction", data: { Zone: 2, HasSkid: false } },
        { name: "pumpManifoldPiping", data: { Zone: 2, HasPiping: false } },
        { name: "stationEnclosure", data: { Zone: 2, HasEnclosure: false } },
        { name: "dropPipeComments", data: { Zone: 2, HasDropPipes: false } },
        { name: "levelControls", data: { Zone: 2, HasLevelControls: false } },
        { name: "pumps", data: { Zone: 2, HasSustain: false, HasMain: false, HasJockey: false, HasWaterFeature: false, HasTransfer: false, HasRecirc: false, HasTrash: false } },
        { name: "skyHarvester", data: { Zone: 2, HasSky: false } },
        { name: "shipLoose", data: { Zone: 2, HasShipLoose: false } },
        { name: "waterTreatment", data: { HasInletFilter: false, HasInletStrainer: false, HasDischargeFilter: false, HasWyeStrainer: false, HasRO: false, HasUV: false, Zone: 2, HasFilters: false } },
        { name: "inletScreen", data: { Zone: 2 } },
        { name: "inletStrainer", data: { Zone: 2 } },
        { name: "dischargeStrainer", data: { Zone: 2 } },
        { name: "dischargeFilter", data: { Zone: 2 } },
        { name: "roSystem", data: { Zone: 2 } },
        { name: "unregulatedPower", data: { Zone: 2, HasUnregulated: false } },
        { name: "regulatedPower", data: { Zone: 2, HasRegulated: false } },
        { name: "sequenceOfOperations", data: { Zone: 2 } },
        { name: "electricalOptions", data: { Zone: 2 } },
        { name: "telemetry", data: { Zone: 2, HasTelemetry: false, HasAccessories: false } },
        { name: "uvSystem", data: { Zone: 2 } },
        { name: "fertigation", data: { Zone: 2, HasFertigation: false } }
      ];

      setLogs(prev => [...prev, `Creating Zone 2 data for ${selectedApiUrls.length} API endpoints...`]);
      
      const results = { successCount: 0, failureCount: 0 };
      
      for (const { name, data } of selectedApiUrls) {
        const url = apiUrls[name];
        
        if (!url) {
          setLogs(prev => [...prev, `Skipping ${name} - URL is undefined`]);
          continue;
        }
        
        const apiName = name;
        setApiStatus(prev => ({ ...prev, [apiName]: "loading" }));
        setLogs(prev => [...prev, `Creating data for ${apiName}...`]);
        
        try {
          await new Promise((resolve, reject) => {
            dispatch(enqueueApiCall(async () => {
              try {
                const response = await axios.post(url, buildZoneCreationPayload(jobDetails, jobId, 2, data));
                
                if (response.status === 200 || response.status === 201) {
                  setApiStatus(prev => ({ ...prev, [apiName]: "success" }));
                  setLogs(prev => [...prev, `Successfully created data for ${apiName}`]);
                  results.successCount++;
                  resolve();
                } else {
                  setApiStatus(prev => ({ ...prev, [apiName]: "failed" }));
                  setLogs(prev => [...prev, `Failed to create data for ${apiName}`]);
                  results.failureCount++;
                  reject(new Error(`Failed to create data for ${apiName}`));
                }
              } catch (error) {
                setApiStatus(prev => ({ ...prev, [apiName]: "failed" }));
                const errorMsg = error.response 
                  ? `Error creating data for ${apiName}: ${error.response.status} ${error.response.statusText}`
                  : `Error creating data for ${apiName}: ${error.message}`;
                setLogs(prev => [...prev, errorMsg]);
                console.error(errorMsg, error);
                results.failureCount++;
                reject(error);
              }
            }));
          });
        } catch (error) {
          console.error(`Error during API call for ${apiName}:`, error);
        }
      }
      
      // Refresh zone count and table IDs
      dispatch(fetchZones(jobId));
      dispatch(fetchTableIds(jobId));
      
      setLogs(prev => [
        ...prev, 
        `Zone 2 creation completed with ${results.successCount} successful and ${results.failureCount} failed operations.`
      ]);
      
      setShowSuccessMessage(true);
      
      // Mark Zone 2 as created so the UI updates immediately
      setZone2Created(true);
      setZone2ExistsState(true); // Also update the API-based state
      
      // Auto-close modal after success
      setTimeout(() => {
        closeModal();
        // Switch to the newly created Zone 2
        dispatch(setSelectedZone(2));
      }, 3000);
      
    } catch (error) {
      console.error("Error creating zone data:", error);
      setLogs(prev => [...prev, `Error creating zone data: ${error.message}`]);
    } finally {
      setCreatingZone(false);
    }
  };

  return (
    <>
      {(roles.Admin || roles.Apps) && (
        <>
          {hasZone2 || zone2Created ? (
            <Button 
              variant={currentActiveZone === 2 ? "primary" : "secondary"} 
              onClick={() => handleZoneClick(2)} 
              className="ms-2"
            >
              Zone 2
            </Button>
          ) : (
            <Button 
              variant="outline-primary" 
              onClick={handleCreateZoneClick} 
              className="create-zone-2-button ms-2"
            >
              Create Zone 2 Data
            </Button>
          )}
        </>
      )}
      
      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Zone 2</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showSuccessMessage && (
            <Alert variant="success">
              Zone 2 created successfully! Switching to Zone 2 view...
            </Alert>
          )}
          
          {logs.length > 0 && logs[0].includes("Cannot create Zone 2") && (
            <Alert variant="warning">
              <strong>Zone Creation Blocked</strong>
              <br />
              {logs.map((log, index) => (
                <div key={index}>{log}</div>
              ))}
            </Alert>
          )}
          
          {(!logs.length || !logs[0].includes("Cannot create Zone 2")) && (
            <p>
              This will create Zone 2 data for this job. The system will verify that Zone 2 
              doesn't already exist and that the maximum record limit hasn't been reached 
              before creating the required data structures with default settings.
            </p>
          )}
          
          {creatingZone && (
            <div className="text-center my-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}
          
          {Object.keys(apiStatus).length > 0 && (
            <div className="mt-3">
              <h5>API Status:</h5>
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                <ul className="list-unstyled">
                  {Object.keys(apiStatus).map((api) => (
                    <li key={api} className="mb-1">
                      {api}... {renderStatusIcon(apiStatus[api])}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="mt-3">
            <h5>Logs:</h5>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '10px', borderRadius: '4px' }}>
              <ul className="list-unstyled">
                {logs.map((log, index) => (
                  <li key={index} className="mb-1">{log}</li>
                ))}
              </ul>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
          {(!logs.length || !logs[0].includes("Cannot create Zone 2")) && (
            <Button 
              variant="primary" 
              onClick={createZoneData} 
              disabled={creatingZone}
            >
              {creatingZone ? (
                <>
                  <Spinner animation="border" size="sm" /> Creating...
                </>
              ) : (
                "Create Zone 2"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CreateZoneButton;
